// ============================================================================
// HAI Reporting Tool - Firestore Service
// CRUD operations for HAI data with Firestore
// ============================================================================

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { db as localDb } from './db';
import type {
  Ward,
  DeviceSnapshot,
  HAIRecord,
  SSISurveillance,
  PatientRecord,
  DeviceDailyCount,
  HCAISurveillanceCase
} from '@/types';

// ============================================================================
// Ward Operations
// ============================================================================

export async function getWards(wardIds?: string[]): Promise<Ward[]> {
  try {
    const snapshot = await getDocs(collection(db, 'wards'));
    const allWards = snapshot.docs.map(doc => convertWard(doc.id, doc.data()));

    // Filter by assigned ward IDs if provided (client-side filtering)
    if (wardIds && wardIds.length > 0) {
      const assignedWardSet = new Set(wardIds);
      return allWards.filter(ward => assignedWardSet.has(ward.id));
    }

    return allWards;
  } catch (error) {
    console.error('Error fetching wards:', error);
    throw error;
  }
}

/**
 * Get all active wards for a specific facility
 * @param facilityId - The facility ID to fetch wards for
 * @returns Array of active wards for the facility
 */
export async function getWardsByFacility(facilityId: string): Promise<Ward[]> {
  try {
    const q = query(
      collection(db, 'wards'),
      where('facilityId', '==', facilityId),
      where('isActive', '==', true),
      orderBy('name')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => convertWard(doc.id, doc.data()));
  } catch (error) {
    console.error('Error fetching wards by facility:', error);
    throw error;
  }
}

export async function getWard(wardId: string): Promise<Ward> {
  try {
    const docRef = doc(db, 'wards', wardId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Ward not found');
    }

    return convertWard(docSnap.id, docSnap.data());
  } catch (error) {
    console.error('Error fetching ward:', error);
    throw error;
  }
}

function convertWard(id: string, data: any): Ward {
  return {
    id,
    facilityId: data.facilityId,
    name: data.name,
    description: data.description || '',
    isActive: data.isActive,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  };
}

// ============================================================================
// Device Snapshot Operations
// ============================================================================

export async function saveDeviceSnapshot(
  data: Omit<DeviceSnapshot, 'id' | 'createdAt' | 'updatedAt'>,
  isUpdate: boolean = false,
  existingId?: string
): Promise<string> {
  try {
    const id = existingId || crypto.randomUUID();
    const docRef = doc(db, 'deviceSnapshots', id);

    // Fetch ward name if not provided (for bridging to deviceDailyCounts)
    let wardName = data.wardName;
    if (!wardName) {
      try {
        const ward = await getWard(data.wardId);
        wardName = ward.name;
      } catch (error) {
        console.warn('Could not fetch ward name for bridging:', error);
      }
    }

    const firestoreData = {
      wardId: data.wardId,
      wardName: wardName || undefined,
      date: data.date,
      patientsWithDevice: data.patientsWithDevice,
      deviceInsertionDates: data.deviceInsertionDates || undefined,
      deviceDaysCounted: data.deviceDaysCounted || undefined,
      createdBy: data.createdBy,
      createdAt: isUpdate ? undefined : serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    if (isUpdate) {
      await updateDoc(docRef, firestoreData as any);
    } else {
      await setDoc(docRef, firestoreData);
    }

    // Save to local DB
    const localData: DeviceSnapshot = {
      ...data,
      wardName,
      id,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    await localDb.deviceSnapshots.put(localData);

    // Bridge to deviceDailyCounts for dashboard visibility
    if (wardName) {
      try {
        await saveDeviceDailyCount(
          wardName,
          data.date,
          {
            cvp: data.patientsWithDevice.cvp,
            pl: data.patientsWithDevice.pl,
            urinaryCatheter: data.patientsWithDevice.urinaryCatheter,
            ventilator: data.patientsWithDevice.ventilator
          }
        );
      } catch (error) {
        console.warn('Could not bridge to deviceDailyCounts:', error);
        // Don't throw - snapshot was saved successfully
      }
    }

    return id;
  } catch (error) {
    console.error('Error saving device snapshot:', error);
    throw error;
  }
}

export async function getDeviceSnapshots(
  wardId: string,
  startDate: string,
  endDate: string
): Promise<DeviceSnapshot[]> {
  try {
    const q = query(
      collection(db, 'deviceSnapshots'),
      where('wardId', '==', wardId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    const results = snapshot.docs.map(doc => convertDeviceSnapshot(doc.id, doc.data()));

    // Cache locally
    for (const result of results) {
      await localDb.deviceSnapshots.put(result);
    }

    return results;
  } catch (error) {
    console.error('Error fetching device snapshots:', error);
    // Try to get from local DB as fallback
    return await localDb.getDeviceSnapshotsByWardAndDateRange(wardId, startDate, endDate);
  }
}

function convertDeviceSnapshot(id: string, data: any): DeviceSnapshot {
  return {
    id,
    wardId: data.wardId,
    wardName: data.wardName || undefined,
    date: data.date,
    patientsWithDevice: data.patientsWithDevice,
    deviceInsertionDates: data.deviceInsertionDates || undefined,
    deviceDaysCounted: data.deviceDaysCounted || undefined,
    createdBy: data.createdBy,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  };
}

// ============================================================================
// HAI Record Operations
// ============================================================================

export async function saveHAIRecord(
  data: Omit<HAIRecord, 'id' | 'createdAt' | 'updatedAt'>,
  isUpdate: boolean = false,
  existingId?: string
): Promise<string> {
  try {
    const id = existingId || crypto.randomUUID();
    const docRef = doc(db, 'haiRecords', id);

    const firestoreData = {
      wardId: data.wardId,
      periodStart: data.periodStart,
      periodEnd: data.periodEnd,
      cases: data.cases,
      createdBy: data.createdBy,
      notes: data.notes || '',
      createdAt: isUpdate ? undefined : serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    if (isUpdate) {
      await updateDoc(docRef, firestoreData as any);
    } else {
      await setDoc(docRef, firestoreData);
    }

    // Save to local DB
    const localData: HAIRecord = {
      ...data,
      id,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    await localDb.haiRecords.put(localData);

    return id;
  } catch (error) {
    console.error('Error saving HAI record:', error);
    throw error;
  }
}

export async function getHAIRecords(
  wardId: string,
  startDate: string,
  endDate: string
): Promise<HAIRecord[]> {
  try {
    const q = query(
      collection(db, 'haiRecords'),
      where('wardId', '==', wardId),
      where('periodStart', '>=', startDate),
      where('periodEnd', '<=', endDate),
      orderBy('periodStart', 'desc')
    );

    const snapshot = await getDocs(q);
    const results = snapshot.docs.map(doc => convertHAIRecord(doc.id, doc.data()));

    // Cache locally
    for (const result of results) {
      await localDb.haiRecords.put(result);
    }

    return results;
  } catch (error) {
    console.error('Error fetching HAI records:', error);
    // Try to get from local DB as fallback
    return await localDb.getHAIRecordsByWardAndDateRange(wardId, startDate, endDate);
  }
}

function convertHAIRecord(id: string, data: any): HAIRecord {
  return {
    id,
    wardId: data.wardId,
    periodStart: data.periodStart,
    periodEnd: data.periodEnd,
    cases: data.cases,
    createdBy: data.createdBy,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    notes: data.notes
  };
}

// ============================================================================
// SSI Surveillance Operations
// ============================================================================

export async function saveSSISurveillance(
  data: Omit<SSISurveillance, 'id' | 'createdAt' | 'updatedAt'>,
  isUpdate: boolean = false,
  existingId?: string
): Promise<string> {
  try {
    const id = existingId || crypto.randomUUID();
    const docRef = doc(db, 'ssiSurveillance', id);

    const firestoreData = {
      wardId: data.wardId,
      periodStart: data.periodStart,
      periodEnd: data.periodEnd,
      procedures: data.procedures,
      infections: data.infections,
      createdBy: data.createdBy,
      notes: data.notes || '',
      createdAt: isUpdate ? undefined : serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    if (isUpdate) {
      await updateDoc(docRef, firestoreData as any);
    } else {
      await setDoc(docRef, firestoreData);
    }

    // Save to local DB
    const localData: SSISurveillance = {
      ...data,
      id,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    await localDb.ssiSurveillance.put(localData);

    return id;
  } catch (error) {
    console.error('Error saving SSI surveillance:', error);
    throw error;
  }
}

export async function getSSISurveillance(
  wardId: string,
  startDate: string,
  endDate: string
): Promise<SSISurveillance[]> {
  try {
    const q = query(
      collection(db, 'ssiSurveillance'),
      where('wardId', '==', wardId),
      where('periodStart', '>=', startDate),
      where('periodEnd', '<=', endDate),
      orderBy('periodStart', 'desc')
    );

    const snapshot = await getDocs(q);
    const results = snapshot.docs.map(doc => convertSSISurveillance(doc.id, doc.data()));

    // Cache locally
    for (const result of results) {
      await localDb.ssiSurveillance.put(result);
    }

    return results;
  } catch (error) {
    console.error('Error fetching SSI surveillance:', error);
    // Try to get from local DB as fallback
    return await localDb.getSSISurveillanceByWardAndDateRange(wardId, startDate, endDate);
  }
}

function convertSSISurveillance(id: string, data: any): SSISurveillance {
  return {
    id,
    wardId: data.wardId,
    periodStart: data.periodStart,
    periodEnd: data.periodEnd,
    procedures: data.procedures,
    infections: data.infections,
    createdBy: data.createdBy,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    notes: data.notes
  };
}

// ============================================================================
// Device Daily Count Operations
// ============================================================================

export async function saveDeviceDailyCount(
  wardName: string,
  date: string,
  counts: { cvp: number; pl: number; urinaryCatheter: number; ventilator: number }
): Promise<void> {
  try {
    const id = `${wardName}_${date}`;
    const docRef = doc(db, 'deviceDailyCounts', id);

    const firestoreData = {
      wardName,
      date,
      cvp: counts.cvp,
      pl: counts.pl,
      urinaryCatheter: counts.urinaryCatheter,
      ventilator: counts.ventilator,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(docRef, firestoreData, { merge: true });

    // Save to local DB
    await localDb.saveDeviceDailyCount({
      id,
      wardName,
      date,
      cvp: counts.cvp,
      pl: counts.pl,
      urinaryCatheter: counts.urinaryCatheter,
      ventilator: counts.ventilator
    });
  } catch (error) {
    console.error('Error saving device daily count:', error);
    throw error;
  }
}

export async function getDeviceDailyCount(wardName: string, date: string): Promise<DeviceDailyCount | null> {
  try {
    const id = `${wardName}_${date}`;
    const docRef = doc(db, 'deviceDailyCounts', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      // Try to get from local DB as fallback
      return await localDb.getDeviceDailyCountByDate(wardName, date) || null;
    }

    const data = convertDeviceDailyCount(docSnap.id, docSnap.data());

    // Cache locally
    await localDb.saveDeviceDailyCount({
      id: docSnap.id,
      wardName: data.wardName,
      date: data.date,
      cvp: data.cvp,
      pl: data.pl,
      urinaryCatheter: data.urinaryCatheter,
      ventilator: data.ventilator
    });

    return data;
  } catch (error) {
    console.error('Error fetching device daily count:', error);
    // Try to get from local DB as fallback
    return await localDb.getDeviceDailyCountByDate(wardName, date) || null;
  }
}

function convertDeviceDailyCount(id: string, data: any): DeviceDailyCount {
  return {
    id,
    wardName: data.wardName,
    date: data.date,
    cvp: data.cvp || 0,
    pl: data.pl || 0,
    urinaryCatheter: data.urinaryCatheter || 0,
    ventilator: data.ventilator || 0,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  };
}

// ============================================================================
// Patient Record Operations
// ============================================================================

export async function savePatientRecord(
  data: Omit<PatientRecord, 'id' | 'createdAt' | 'updatedAt'>,
  isUpdate: boolean = false,
  existingId?: string
): Promise<string> {
  try {
    const id = existingId || crypto.randomUUID();
    const docRef = doc(db, 'patientRecords', id);

    const firestoreData = {
      wardName: data.wardName,
      gender: data.gender || null,
      patientIdentifier: data.patientIdentifier || '',
      age: data.age || null,
      admissionDate: data.admissionDate,
      recordDate: data.recordDate,
      dischargeDate: data.dischargeDate || null,
      clinicalDiagnosis: data.clinicalDiagnosis || null,
      riskFactors: data.riskFactors || [],
      devices: data.devices || [],
      hcaiCases: data.hcaiCases || data.haiCases || [],
      outcome: data.outcome || null,
      outcomeDatetime: data.outcomeDatetime || null,
      transferredFacility: data.transferredFacility || null,
      createdBy: data.createdBy,
      createdAt: isUpdate ? undefined : serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    if (isUpdate) {
      await updateDoc(docRef, firestoreData as any);
    } else {
      await setDoc(docRef, firestoreData);
    }

    // Save to local DB
    const localData: PatientRecord = {
      ...data,
      id,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    await localDb.patientRecords.put(localData);

    // Auto-compute and save daily device counts for recordDate
    await computeAndSaveDeviceDailyCounts(data.wardName, data.recordDate);

    return id;
  } catch (error) {
    console.error('Error saving patient record:', error);
    throw error;
  }
}

/**
 * Compute and save daily device counts for a specific date
 * Aggregates all patient devices that overlap with the given date
 */
async function computeAndSaveDeviceDailyCounts(wardId: string, date: string): Promise<void> {
  try {
    // Get all patients for this ward
    const patients = await getPatientsByWard(wardId);

    // Count devices for the given date
    const counts = {
      cvp: 0,
      pl: 0,
      urinaryCatheter: 0,
      ventilator: 0
    };

    for (const patient of patients) {
      // Check if patient was admitted on or before this date
      if (patient.admissionDate <= date) {
        // Check if patient was discharged after this date (or still admitted)
        const isStillAdmitted = !patient.dischargeDate || patient.dischargeDate >= date;

        if (isStillAdmitted) {
          // Count devices that were inserted on or before this date
          for (const device of patient.devices) {
            if (device.insertionDate <= date) {
              // Check if device was removed after this date (or still in use)
              const isDeviceStillInUse = !device.removalDate || device.removalDate >= date;

              if (isDeviceStillInUse) {
                counts[device.deviceType as keyof typeof counts]++;
              }
            }
          }
        }
      }
    }

    // Save the computed counts
    await saveDeviceDailyCount(wardId, date, counts);
  } catch (error) {
    console.error('Error computing device daily counts:', error);
    // Don't throw - this is a background computation
  }
}

export async function getPatientRecord(patientId: string): Promise<PatientRecord> {
  try {
    const docRef = doc(db, 'patientRecords', patientId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Patient record not found');
    }

    const data = convertPatientRecord(docSnap.id, docSnap.data());

    // Cache locally
    await localDb.patientRecords.put(data);

    return data;
  } catch (error) {
    console.error('Error fetching patient record:', error);
    // Try to get from local DB as fallback
    const localRecord = await localDb.patientRecords.get(patientId);
    if (localRecord) {
      return localRecord;
    }
    throw error;
  }
}

export async function getPatientsByWard(wardName: string): Promise<PatientRecord[]> {
  try {
    const q = query(
      collection(db, 'patientRecords'),
      where('wardName', '==', wardName),
      orderBy('admissionDate', 'desc')
    );

    const snapshot = await getDocs(q);
    const results = snapshot.docs.map(doc => convertPatientRecord(doc.id, doc.data()));

    // Cache locally
    for (const result of results) {
      await localDb.patientRecords.put(result);
    }

    return results;
  } catch (error) {
    console.error('Error fetching patients for ward:', error);
    // Try to get from local DB as fallback
    return await localDb.patientRecords.where('wardName').equals(wardName).toArray();
  }
}

export async function getPatientsByWardAndDateRange(
  wardName: string,
  startDate: string,
  endDate: string
): Promise<PatientRecord[]> {
  try {
    const q = query(
      collection(db, 'patientRecords'),
      where('wardName', '==', wardName),
      where('admissionDate', '>=', startDate),
      where('admissionDate', '<=', endDate),
      orderBy('admissionDate', 'desc')
    );

    const snapshot = await getDocs(q);
    const results = snapshot.docs.map(doc => convertPatientRecord(doc.id, doc.data()));

    // Cache locally
    for (const result of results) {
      await localDb.patientRecords.put(result);
    }

    return results;
  } catch (error) {
    console.error('Error fetching patients for date range:', error);
    // Try to get from local DB as fallback
    return await localDb.patientRecords.where('wardName').equals(wardName).toArray();
  }
}

export async function updatePatientRecord(
  patientId: string,
  data: Partial<Omit<PatientRecord, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<void> {
  try {
    // Get existing record first
    const existingRecord = await getPatientRecord(patientId);

    // Merge with existing data
    const mergedData = {
      ...existingRecord,
      ...data,
      updatedAt: new Date()
    };

    // Save with update flag
    await savePatientRecord(
      {
        wardName: mergedData.wardName,
        gender: mergedData.gender,
        patientIdentifier: mergedData.patientIdentifier,
        age: mergedData.age,
        admissionDate: mergedData.admissionDate,
        recordDate: mergedData.recordDate,
        dischargeDate: mergedData.dischargeDate,
        clinicalDiagnosis: mergedData.clinicalDiagnosis,
        riskFactors: mergedData.riskFactors,
        devices: mergedData.devices,
        hcaiCases: mergedData.hcaiCases || mergedData.haiCases,
        outcome: mergedData.outcome,
        outcomeDatetime: mergedData.outcomeDatetime,
        transferredFacility: mergedData.transferredFacility,
        createdBy: mergedData.createdBy
      },
      true,
      patientId
    );
  } catch (error) {
    console.error('Error updating patient record:', error);
    throw error;
  }
}

export async function deletePatientRecord(patientId: string): Promise<void> {
  try {
    const docRef = doc(db, 'patientRecords', patientId);

    // Delete from Firestore
    await updateDoc(docRef, {
      deletedAt: serverTimestamp()
    });

    // Delete from local DB
    await localDb.patientRecords.delete(patientId);
  } catch (error) {
    console.error('Error deleting patient record:', error);
    throw error;
  }
}

function convertPatientRecord(id: string, data: any): PatientRecord {
  return {
    id,
    wardName: data.wardName,
    gender: data.gender || undefined,
    patientIdentifier: data.patientIdentifier || undefined,
    age: data.age || undefined,
    admissionDate: data.admissionDate,
    recordDate: data.recordDate,
    dischargeDate: data.dischargeDate || undefined,
    clinicalDiagnosis: data.clinicalDiagnosis || undefined,
    riskFactors: data.riskFactors || undefined,
    devices: data.devices || [],
    hcaiCases: data.hcaiCases || data.haiCases || [], // Support both old and new field names
    outcome: data.outcome || undefined,
    outcomeDatetime: data.outcomeDatetime || undefined,
    transferredFacility: data.transferredFacility || undefined,
    createdBy: data.createdBy,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  };
}

// ============================================================================
// HCAI Surveillance Case Operations
// ============================================================================

export async function saveHCAICase(data: any): Promise<string> {
  try {
    const id = crypto.randomUUID();
    const docRef = doc(db, 'hcaiCases', id);

    const firestoreData = {
      // Case Identification
      district: data.district || '',
      subDistrict: data.subDistrict || '',
      hospital: data.hospital || '',
      department: data.department || '',
      patientName: data.patientName || '',
      hospitalNumber: data.hospitalNumber || '',
      transferredIn: data.transferredIn || false,
      transferringFacility: data.transferringFacility || '',
      gender: data.gender || null,
      age: data.age || null,
      ward: data.ward || '',
      admissionDate: data.admissionDate,

      // Clinical Data
      clinicalDiagnosis: data.clinicalDiagnosis || '',
      riskFactors: data.riskFactors || {},
      devices: data.devices || [],
      antibiotics: data.antibiotics || [],

      // Microbiological Data
      microbiological: data.microbiological || [],

      // Outcome Data
      infections: data.infections || [],
      patientOutcome: data.patientOutcome || null,
      outcomeDate: data.outcomeDate || '',
      transferredToFacility: data.transferredToFacility || '',

      // Reporting
      reportedBy: data.reportedBy || '',
      reporterRank: data.reporterRank || '',
      reportDate: data.reportDate || '',

      // Metadata
      createdBy: data.createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(docRef, firestoreData);

    // Cache to local IndexedDB
    const localData = {
      ...data,
      id,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    await localDb.hcaiCases.put(localData);

    console.log('[saveHCAICase] Saved to Firestore and cached locally:', id);

    return id;
  } catch (error) {
    console.error('Error saving HCAI case:', error);
    throw error;
  }
}

/**
 * Fetch all HCAI surveillance cases from Firestore
 */
export async function getHCAICases(): Promise<any[]> {
  try {
    const hcaiQuery = query(
      collection(db, 'hcaiCases'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(hcaiQuery);
    const results = snapshot.docs.map(doc => {
      const data = doc.data() as any;
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      } as HCAISurveillanceCase;
    });

    // Cache all to local DB
    for (const result of results) {
      await localDb.hcaiCases.put(result);
    }

    console.log('[getHCAICases] Fetched and cached', results.length, 'cases');

    return results;
  } catch (error) {
    console.error('Error fetching HCAI cases:', error);
    throw error;
  }
}
